import { z } from "zod";
import { localStore } from "../../../lib/store";

type PushDevice = "ios" | "android" | "web";

/**
 * Last FCM token successfully synced with the backend for the web app.
 * Used by `shouldAttemptPushSync` to skip redundant server calls.
 */
export const webPushFbTokenStore = localStore<string | null>(
  "fb_token",
  null,
  z.string().nullable()
);

/**
 * Last FCM token successfully synced with the backend for iOS/Android native
 * webview. Mirrors the token value the native bridge surfaced via
 * `androidGetFirebaseToken` / `iosGetFirebaseToken`.
 */
export const nativePushTokenStore = localStore<string | null>(
  "native_push_token",
  null,
  z.string().nullable()
);

/**
 * Flag written by the native Android bridge during push-token probe retries.
 * We don't produce it from the webapp — we just clear it on logout so the next
 * login triggers a fresh probe.
 */
export const nativePushProbeAttemptedStore = localStore(
  "native_push_probe_attempted",
  false,
  z.boolean()
);

/**
 * Tracks whether we've run the one-shot native FCM repair on this install.
 * The repair calls `FirebaseInstallations.delete` so the SDK mints a fresh
 * FID (the iOS Keychain persists the FID across uninstall and can carry over
 * a stale APNs binding that silently breaks push delivery).
 *
 * Absent value triggers the repair. This happens in three cases:
 *   1. Fresh install (no prior state).
 *   2. Reinstall (localStorage wiped on uninstall).
 *   3. Existing install that never saw this rollout (the key is brand-new).
 *
 * Case 3 means the repair runs fleet-wide once at rollout, not strictly on
 * reinstall — by design, since we can't tell case 3 apart from case 2 from
 * the JS layer, and any FID-corrupted install benefits from the repair.
 * Versioned key (_v1) lets us re-trigger fleet-wide later by bumping the
 * suffix.
 */
export const nativeFcmFreshnessStore = localStore(
  "native_fcm_freshness_v1",
  false,
  z.boolean()
);

/**
 * Timestamp of the last in-app push permission prompt dismissal — set when the
 * user clicks "Maybe later", or after a "Turn on Notifications" attempt, or
 * after an "Open Settings" deep-link click. Gates `NotificationFixupPage`:
 *   - Web: dismissal is final (the browser permission and this flag live and
 *     die together — no reinstall reset — so re-asking would only harass users
 *     who already made their choice).
 *   - Native: dismissal grants a cooldown before re-prompting, since an OS
 *     reinstall resets the permission but not the account. localStorage is
 *     wiped on uninstall, so a fresh reinstall always re-asks.
 * Null = never dismissed on this install.
 */
export const notificationPromptDismissedAtStore = localStore<number | null>(
  "notification_prompt_dismissed_at_v1",
  null,
  z.number().nullable()
);

/** Cooldown after dismissal before we may re-prompt the user (native only). */
export const NOTIFICATION_PROMPT_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

export function getPushTokenStore(device: PushDevice) {
  return device === "web" ? webPushFbTokenStore : nativePushTokenStore;
}
